import { GetMedication } from "@/services/terminology";
import { stampReceituarioProfile } from "src-next/modules/sncr";

const CMED_TAG_MATCHERS = [
  { name: "VERMELHA" },
  { name: "PRETA" },
  { name: "AMARELA" },
  { name: "AZUL" },
  { name: "SEM TARJA" },
  { name: "ESPECIAL" },
];

/**
 * Subconjunto do catálogo `unit` de MedicationFields — só para alinhar formValue
 * no item hidratado (sem exportar/alterar MedicationFields).
 */
const MEDICATION_FORM_UNITS = [
  { value: "GRAN", label: "Grânulos" },
  { value: "COM", label: "Comprimido" },
  { value: "CAP", label: "Cápsula" },
  { value: "SOL OR", label: "Solução Oral" },
  { value: "SOL INJ", label: "Solução Injetável" },
  { value: "SOL", label: "Solução" },
  { value: "SUSP", label: "Suspensão" },
  { value: "POM", label: "Pomada" },
  { value: "GEL", label: "Gel" },
  { value: "XAR", label: "Xarope" },
  { value: "INJ", label: "Injetável" },
  { value: "COL", label: "Colírio" },
  { value: "AER", label: "Aerossol" },
  { value: "SUP", label: "Supositório" },
  { value: "AMP", label: "Ampola" },
  { value: "CTG", label: "Cartucho" },
  { value: "ENV", label: "Envelope ou Sachê" },
  { value: "BISN", label: "Bisnaga" },
];

function extractExtension(resource: any, fragment: string): string | undefined {
  return resource?.extension?.find((ext: any) =>
    ext?.url?.includes(fragment)
  )?.valueString;
}

function resolveTagFromCmed(tarjaValue?: string): string {
  if (!tarjaValue || tarjaValue === "-") return "Branca";
  const matched = CMED_TAG_MATCHERS.find((tag) =>
    tarjaValue.toUpperCase().includes(tag.name)
  );
  if (!matched || matched.name === "SEM TARJA") return "Branca";
  return matched.name;
}

function matchFormUnit(formText?: string): { value?: string; label: string } {
  if (!formText) return { label: "" };
  const upper = formText.toUpperCase();
  const found = MEDICATION_FORM_UNITS.find(
    (u) =>
      upper.includes(u.value.trim().toUpperCase()) ||
      formText.toLowerCase().includes(u.label.toLowerCase())
  );
  if (found) return { value: found.value, label: found.label };
  return { label: formText };
}

/**
 * Enriquece um item de medicação (já mapeado do MedicationRequest) com CMED
 * via GetMedication(code). Falha → devolve o item original.
 *
 * Limitação: type permanece "medication" (FHIR/CMED desta trilha não distingue
 * supplement/herbal de forma confiável para a hidratação).
 */
export async function enrichMedicationInterventionFromCmed(
  item: any
): Promise<any> {
  const code =
    item?.code?.coding?.[0]?.code ||
    item?.inputValue?.code?.coding?.[0]?.code;

  if (!code) {
    // Sem coding: só tenta alinhar formValue ao catálogo local
    return {
      ...item,
      formValue: matchFormUnit(item?.formValue?.label || item?.formValue),
    };
  }

  try {
    const response = await GetMedication(undefined, undefined, code);
    const resource = response?.data?.entry?.[0]?.resource;
    if (!resource) return item;

    const tarjaRaw = extractExtension(resource, "Tarja");
    const presentation = extractExtension(resource, "Apresentacao");
    const substance = extractExtension(resource, "Substancia");
    const productName =
      extractExtension(resource, "Produto") || item?.name || "";
    const therapeuticClass = extractExtension(resource, "ClasseTerapeutica");
    const productType = extractExtension(resource, "TipoProduto");

    const tag = resolveTagFromCmed(tarjaRaw);
    const label = `${productName}${
      presentation ? ` - ${presentation}` : ""
    }`.trim();

    const coding = resource?.code || item?.code;
    // Forma da posologia: preferir dosage FHIR (como generateItem); apresentação CMED só no label
    const formValue = matchFormUnit(item?.formValue?.label || "");

    return stampReceituarioProfile({
      ...item,
      name: productName || item?.name,
      label,
      tag,
      formValue,
      // Mantém `form` (posologia) da hidratação — não misturar Apresentacao CMED no PDF
      inputValue: {
        ...item?.inputValue,
        name: productName || item?.inputValue?.name,
        label,
        presentation: presentation || item?.inputValue?.presentation,
        description: substance || item?.inputValue?.description || "-",
        class: therapeuticClass || item?.inputValue?.class || "-",
        type: productType || item?.inputValue?.type || "-",
        tag,
        code: coding,
      },
      code: coding,
      // type: mantém "medication" — ver limitação no JSDoc
    });
  } catch (err) {
    console.warn(
      "enrichMedicationInterventionFromCmed: falha GetMedication, mantendo FHIR básico",
      err
    );
    return {
      ...item,
      formValue: matchFormUnit(item?.formValue?.label || item?.formValue),
    };
  }
}

/**
 * Enriquece em paralelo só itens type medication/supplement/herbalMedicine.
 * allSettled: falha individual não zera a lista.
 */
export async function enrichMedicationsFromCmed(
  interventions: any[]
): Promise<any[]> {
  if (!interventions?.length) return interventions || [];

  const results = await Promise.allSettled(
    interventions.map(async (item) => {
      const isMedicationType =
        item?.type === "medication" ||
        item?.type === "supplement" ||
        item?.type === "herbalMedicine";

      if (!isMedicationType) return item;
      return enrichMedicationInterventionFromCmed(item);
    })
  );

  return results.map((result, index) => {
    if (result.status === "fulfilled") return result.value;
    console.warn(
      "enrichMedicationsFromCmed: item falhou, usando mapeamento FHIR",
      result.reason
    );
    return interventions[index];
  });
}
